import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { Skeleton } from '@mui/material'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import CustomUserCard from 'features/users/components/CustomUserCard'
import { SnackbarProvider, closeSnackbar, enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useFetchDates from '../hooks/useFetchDates'
import '../styles.scss'
import Calendar from './Calendar'

import {
  selectDatosSolicitud,
  selectVacaciones,
  setDatosSolicitud,
} from 'features/solicitudes/solicitudesSlice'
import { useLocation, useNavigate } from 'react-router-dom'

const CalendarioAnual = ({
  currentYear = new Date().getFullYear(),
  setCurrentYear = null,
  useUser = [null, () => {}],
  vacacionesPendientes = null,
  vacacionesTipoDias = null,
  allowSelection = false,
  fechasSolicitud = [],
}) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const [user, setUser] = useUser
  const { response, loading, error } = useFetchDates({
    year: currentYear,
    user: user,
  })
  const [fechas, setFechas] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [requestError, setRequestError] = useState(null)

  const vacaciones = useSelector(selectVacaciones)

  // const [pendientes, setPendientes] = useState(vacaciones.pendientes)
  const [tipoDias, setTipoDias] = useState(vacaciones.tipo_dias)

  const datos = useSelector(selectDatosSolicitud)

  useEffect(() => {
    for (const fecha of fechasSolicitud) {
      const formattedDate = fecha.split('T')[0]

      const date = new Date(formattedDate)
      response.unshift({
        color: '#fad101',
        name: 'EN SOLICITUD',
        situacion: 'S',
        tipo: 'S',
        startDate: date,
        endDate: date,
        rendering: 'background',
      })
    }

    setFechas(response)
  }, [response])

  useEffect(() => {
    setIsLoading(loading)
  }, [loading])

  useEffect(() => {
    if (error)
      return setRequestError({
        type: 'error',
        text:
          error?.response?.status === 500 ? `Ha ocurrido un error en el servidor` : error.message,
      })
    setRequestError(null)
  }, [error])

  useEffect(() => {
    if (datos) {
      const _datos = { ...datos }
      for (const idpk in _datos) {
        if (Object.hasOwnProperty.call(_datos, idpk)) {
          _datos[idpk] = null
        }
      }
      for (const fecha of fechas) {
        if (fecha.tipo === 'seleccion') {
          for (const idpk in _datos) {
            if (Object.hasOwnProperty.call(_datos, idpk)) {
              if (!_datos[idpk]) {
                _datos[idpk] = fecha.startDate
                break
              }
            }
          }
        }
      }
      dispatch(setDatosSolicitud(_datos))
    }
  }, [fechas])

  const ahora = new Date()
  const minDate = new Date(ahora.getFullYear() - 1, 0, 1)
  const maxDate = new Date(ahora.getFullYear() + 1, 11, 31)

  const selectedRange = (rango) => {
    const _fechas = rango.calendar.getDataSource()
    const ahora = new Date()

    if (rango.startDate.getTime() <= rango.endDate.getTime()) {
      const nuevas_fechas = []

      for (
        let d = new Date(rango.startDate.getTime());
        d.getTime() <= rango.endDate.getTime();
        d.setDate(d.getDate() + 1)
      ) {
        if (d.getTime() < ahora.getTime()) {
          enqueueSnackbar(`No se pueden seleccionar fechas anteriores al día de hoy`)
          return
        }
        const _fecha = new Date(d.getTime())
        _fecha.setHours(1)

        // Si ya estaba seleccionada la fecha la quita
        const index = _fechas.findIndex(
          (f) => f.startDate.getTime() === _fecha.getTime() && f.tipo === 'seleccion',
        )
        if (index > -1) {
          _fechas.splice(index, 1)
          continue
        }

        // Comprueba el tipo de día que va a seleccionar
        const diaSeleccionado = _fechas.filter(
          (f) =>
            f.startDate.getFullYear() === _fecha.getFullYear() &&
            f.startDate.getMonth() === _fecha.getMonth() &&
            f.startDate.getDate() === _fecha.getDate() &&
            f.tipo !== 'seleccion',
        )[0]

        const tipoDiaSeleccionado = diaSeleccionado ? diaSeleccionado.tipo : 'L'
        // Si el dia ya esta solicitado o con vacaciones aprobadas
        if (tipoDiaSeleccionado === 'S' || diaSeleccionado?.situacion === 'VAC') continue
        if (tipoDias === 'X' && tipoDiaSeleccionado === 'F') continue
        if (tipoDias === 'L' && tipoDiaSeleccionado !== 'L') continue

        // No permite seleccionar más días de los que le quedan pendientes
        const seleccionados =
          _fechas.filter((f) => f.tipo === 'seleccion').length + nuevas_fechas.length

        const pendientes = Number.parseInt(vacaciones.pendientes, 10)

        if (pendientes && seleccionados && pendientes - seleccionados <= 0) {
          enqueueSnackbar(`No le quedan más días disponibles`)
          break
        }

        nuevas_fechas.push({
          name: 'Selección',
          startDate: _fecha,
          endDate: _fecha,
          color: 'yellow',
          tipo: 'seleccion',
          seleccion: 'seleccion',
          rendering: 'background',
        })
      }
      const seleccion = [..._fechas, ...nuevas_fechas]
      setFechas(seleccion)
      rango.calendar.setDataSource(seleccion)
    }
  }

  const changeYear = (year) => {
    setCurrentYear(year.currentYear)
  }

  const addTooltip = (element, events) => {
    let tooltip = ''
    if (events.length > 0) {
      events.forEach((event) => {
        tooltip += `${event.name}\n`
      })
      element.dataset.tooltip = tooltip
      element.setAttribute('tabindex', '0')
    }
  }

  const handleDayClick = (e) => {
    for (const i of (e && e.events) || []) {
      if (i.origen_solicitud && i.codigo_solicitud) {
        if (!location.pathname.startsWith('/solicitud/')) {
          navigate(`/solicitud/?codigo=${i.codigo_solicitud}&origen=${i.origen_solicitud}`)
        }
      }
    }
  }

  const fechas_ds = fechas

  const applyRendering = (elemento, evento) => {
    switch (evento.rendering) {
      case 'background':
        elemento.style.backgroundColor = evento.color
        if (evento.textColor) {
          elemento.style.color = evento.textColor
        }
        break

      case 'border':
        elemento.style.boxShadow = `${evento.color} 0px -4px 0px 0px inset`
        break

      case 'number':
        elemento.style.color = evento.color
        elemento.style.fontWeight = 'bolder'
        break

      default:
        break
    }
  }

  const renderDay = (elemento, fecha, evento) => {
    if (!evento?.length) return

    const firstEvent = evento[0]
    const lastEvent = evento[evento.length - 1]

    // Render inicial del calendario
    applyRendering(elemento, firstEvent)

    // Render de la última solicitud (sobrescribe si aplica)
    if (lastEvent !== firstEvent) {
      applyRendering(elemento, lastEvent)
    }

    // Prioridad máxima: selección
    if (evento.some((ev) => ev.seleccion === 'seleccion')) {
      elemento.style.backgroundColor = 'yellow'
    }

    elemento.style.borderRadius = 0
  }

  return (
    <>
      <SnackbarProvider
        maxSnack={5}
        autoHideDuration={6000}
        action={(snackbarId) => (
          <IconButton onClick={() => closeSnackbar(snackbarId)} aria-label="cerrar alerta">
            <CloseRoundedIcon />
          </IconButton>
        )}
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        variant={'error'}
      />
      {user?.id ? (
        <CustomUserCard
          returnToUser={() => {
            setUser(null)
          }}
          loading={isLoading}
          user={user}
          message={requestError ?? null}
        />
      ) : (
        <></>
      )}
      {isLoading & !user ? (
        <Skeleton height={400} />
      ) : (
        <>
          <Box
            sx={{
              flexGrow: 1,
              mt: 4,
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Typography
              variant="caption"
              gutterBottom
              sx={{
                padding: '2px 10px',
                boxShadow: '#ff7800 0px -4px 0px 0px inset',
              }}
            >
              No Laboral
            </Typography>

            <Typography
              variant="caption"
              gutterBottom
              sx={{ padding: '2px 10px', backgroundColor: '#ff7800' }}
            >
              Festivo
            </Typography>

            <Typography
              variant="caption"
              gutterBottom
              sx={{ padding: '2px 10px', backgroundColor: '#00a1ad' }}
            >
              Vacaciones disfrutadas
            </Typography>

            <Typography
              variant="caption"
              gutterBottom
              sx={{ padding: '2px 10px', boxShadow: '#00a1ad 0px -4px 0px 0px inset' }}
            >
              Vacaciones autorizadas
            </Typography>

            <Typography
              variant="caption"
              gutterBottom
              sx={{
                padding: '2px 10px',
                boxShadow: '#fad101 0px -4px 0px 0px inset',
              }}
            >
              Vacaciones solicitadas
            </Typography>
            <Typography
              variant="caption"
              gutterBottom
              sx={{ padding: '2px 10px', backgroundColor: '#cf0909', color: '#000' }}
            >
              Permisos disfrutados
            </Typography>

            <Typography
              variant="caption"
              gutterBottom
              sx={{
                padding: '2px 10px',
                boxShadow: '#cf0909 0px -4px 0px 0px inset',
              }}
            >
              Permisos autorizados
            </Typography>
            <Typography
              variant="caption"
              gutterBottom
              sx={{
                padding: '2px 10px',
                boxShadow: '#d900ff 0px -4px 0px 0px inset',
              }}
            >
              Permisos solicitados
            </Typography>

            {fechasSolicitud && fechasSolicitud.length > 0 ? (
              <Typography
                variant="caption"
                gutterBottom
                sx={{ padding: '2px 10px', backgroundColor: '#fad101' }}
              >
                EN SOLICITUD
              </Typography>
            ) : (
              <Typography
                variant="caption"
                gutterBottom
                sx={{
                  padding: '2px 10px',
                  boxShadow: '#acc433 0px -4px 0px 0px inset',
                }}
              >
                ITs
              </Typography>
            )}
          </Box>
          <Calendar
            language="es"
            enableRangeSelection={allowSelection}
            onDayEnter={(event) => {
              addTooltip(event.element, event.events)
            }}
            roundRangeLimits={false}
            enableContextMenu={true}
            minDate={minDate}
            maxDate={maxDate}
            year={currentYear}
            onRangeSelected={selectedRange}
            onYearChanged={changeYear}
            onDayClick={handleDayClick}
            style="custom"
            dataSource={fechas_ds}
            customDataSourceRenderer={(el, d, ev) => {
              renderDay(el, d, ev)
            }}
          />
        </>
      )}
    </>
  )
}

export default CalendarioAnual
